import { Fragment, useState, useContext } from "react";
import { Localization } from "@viamap/viamap2-common";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import JSZip from "jszip";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { ViewButtonCheck, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { message } from "./Message";
import { PropertyData } from "src/propertyInfoTemplates/PropertyInfoCollectorTypes";
import { Feature } from "src/states/ApplicationStateFeatures";
import { ApplicationStateContext } from "src/states/ApplicationState";
import { filterLatestEnergyLabels } from "src/components/EnergyLabelComponents";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { getTingbogPdfUrl } from "src/managers/TingbogDownload";

interface DocumentsComponentProps {
   data: PropertyData;
}

export type documentData = {
   id: string;
   title: string;
   type: "url" | "blob";
   getUrl?: () => string;
   getBlob?: () => Promise<Blob>;
   returnDoc: "pdf" | "png" | "None";
   feature?: Feature;
   useProxy?: boolean;
   category?: string;
   // mark whether this document can be selected for bulk download
   selectable?: boolean;
};

export function DocumentsComponent({ data }: DocumentsComponentProps) {
   // access feature-check helper from application state so we can guard programmatic actions
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   // Helper: Unicode-safe base64 encode
   function base64EncodeUnicode(str: string): string {
      return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
         String.fromCharCode(parseInt(p1, 16))
      ));
   }

   // Helper: Convert SVG string to PNG Blob using Canvas
   const svgStringToPngBlob = async (svgString: string, width = 800, height = 800): Promise<Blob> => {
      return new Promise((resolve, reject) => {
         try {
            const img = new window.Image();
            const svgBase64 = base64EncodeUnicode(svgString);
            const svg = `data:image/svg+xml;base64,${svgBase64}`;
            img.onload = function () {
               try {
                  const canvas = document.createElement("canvas");
                  canvas.width = width;
                  canvas.height = height;
                  const ctx = canvas.getContext("2d");
                  if (!ctx) return reject("Canvas context error");
                  ctx.drawImage(img, 0, 0, width, height);
                  canvas.toBlob(blob => blob ? resolve(blob) : reject("PNG conversion failed"), "image/png");
               } catch (canvasErr) {
                  reject(canvasErr);
               }
            };
            img.onerror = function () {
               reject("SVG image load error");
            };
            img.src = svg;
         } catch (err) {
            reject(err);
         }
      });
   };
   const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
   const [docErrors, setDocErrors] = useState<Record<string, string>>({});
   const [isDownloading, setIsDownloading] = useState(false);
   const bfeNr = data?.EBRBFE?.bestemtFastEjendomBFENr ?? data.GQL_BFE?.MAT_SamletFastEjendom.nodes?.[0]?.BFEnummer ?? "";
   const matriklen = data.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0]?.jordstykkeSamlesISamletFastEjendom?.nodes ?? [];

   const availableDocuments: documentData[] = [
      ...(matriklen?.map?.((e) => {
         const matrikelNr = e?.matrikelnummer || "";
         const ejerlav = e?.jordstykkeLiggerIEjerlav?.ejerlavskode || "";
         return {
            id: `jordforureningsattest-${ejerlav}-${matrikelNr}`,
            title: `Jordforureningsattest for Matrikel ${matrikelNr}`,
            type: "url",
            getUrl: () => `https://jord.miljoeportal.dk/report/generate/?elav=${encodeURIComponent(String(ejerlav))}&matrnr=${encodeURIComponent(String(matrikelNr))}`,
            category: 'Miljø',
            useProxy: false,
            returnDoc: "pdf",
            selectable: true,
            feature: Feature.Jordforureningsattest,
         } as documentData;
      }) ?? []),
      {
         id: 'tingbogsattest',
         title: 'Tingbogsattest',
         type: 'url',
         getUrl: () => 'https://www.tinglysning.dk/tmv/landingpage',
         category: 'Tingbogsattest',
         useProxy: false,
         returnDoc: "None"
      },
      {
         id: `vurderingsmeddelelse_${bfeNr}`,
         title: `Vurderingsmeddelelse (${bfeNr})`,
         type: 'url',
         getUrl: () => `https://www.ois.dk/search/${bfeNr}/sfe/svur/vurderingsmeddelelse`,
         category: 'Vurdering',
         useProxy: false,
         returnDoc: "None"
      },
      // (BBR Kortudsnit entry is conditionally added below if the user has access)
      {
         id: `bbr-kort-${bfeNr}`,
         title: `BBR Kort`,
         type: 'url',
         getUrl: () => `https://kort.bbr.dk/?bfe=${bfeNr}`,
         category: 'BBR',
         useProxy: false,
         returnDoc: "None"
      },
      {
         id: `bbr-oplysninger-${bfeNr}`,
         title: `BBR Oplysninger`,
         type: 'url',
         getUrl: () => `https://bbr.dk/se-bbr-oplysninger/0/1/0/bfe:${bfeNr}`,
         category: 'BBR',
         useProxy: false,
         returnDoc: "None"
      },
      {
         id: `bbr-meddelelse-${bfeNr}`,
         title: `BBR Meddelelse`,
         type: 'url',
         getUrl: () => `https://bbr.dk/pls/wwwdata/get_newois_pck.show_bbr_meddelelse_pdf?i_bfe=${bfeNr}`,
         category: 'BBR',
         useProxy: true,
         returnDoc: 'pdf',
         selectable: true
      }
   ]
   // Add Lokalplan documents directly into the availableDocuments list.
   data.PlanDataLokalPlan?.features?.forEach((feature) => {
      const plan = feature.properties as any;
      if (plan && plan.doklink) {
         availableDocuments.push({
            id: `lokalplan-${plan.id}`,
            title: `Lokalplan - ${plan.plannavn || plan.id}`,
            type: 'url',
            getUrl: () => plan.doklink,
            category: 'Planer',
            useProxy: false,
            returnDoc: 'pdf',
            selectable: true
         });
      }
   });

   // Add Kommuneplan documents directly into availableDocuments
   data?.PlanDataKommunePlan?.features?.forEach((feature: any) => {
      const plan = feature.properties as any;
      if (plan && plan.doklink) {
         availableDocuments.push({
            id: `kommuneplan-${plan.id}`,
            title: `Kommuneplan - ${plan.plannavn || plan.id}`,
            type: 'url',
            getUrl: () => plan.doklink,
            category: 'Planer',
            useProxy: false,
            returnDoc: 'pdf',
            selectable: true,
         });
      }
   });

   // Add cancelled (aflyst) lokalplan documents directly into availableDocuments
   data?.PlanDataLokalPlanAflyst?.features?.forEach((feature: any) => {
      const plan = feature.properties as any;
      if (plan && plan.doklink) {
         availableDocuments.push({
            id: `lokalplan-aflyst-${plan.oid}`,
            title: `Aflyst Lokalplan - ${plan.plannavn || plan.oid}`,
            type: 'url',
            getUrl: () => plan.doklink,
            category: 'Aflyste Planer',
            useProxy: false,
            returnDoc: 'pdf',
            selectable: true,
         });
      }
   });

   // Add cancelled kommuneplan documents directly into availableDocuments
   data?.PlanDataKommunePlanAflyst?.features?.forEach((feature: any) => {
      const plan = feature.properties as any;
      if (plan && plan.doklink) {
         availableDocuments.push({
            id: `kommuneplan-aflyst-${plan.oid}`,
            title: `Aflyst Kommuneplan - ${plan.plannavn || plan.oid}`,
            type: 'url',
            getUrl: () => plan.doklink,
            category: 'Aflyste Planer',
            useProxy: false,
            returnDoc: 'pdf',
            selectable: true,
         });
      }
   });

   // Add servitutter (tingbog entries) directly into availableDocuments
   const stamOplysninger = data?.ViamapTingBog?.stamOplysninger;
   data?.ViamapTingBog?.servitutter?.forEach((servitut: any) => {
      const tingbogsLink = getTingbogPdfUrl(servitut, stamOplysninger);
      if (tingbogsLink) {
         function TingBogRecordNumber(a: string) {
            try {
               return (a || '').replace(/(.{4})(.{2})(.{2})-(.*)/, "$3$2$1-$4");
            } catch {
               return a || '';
            }
         }
         const recordNo = TingBogRecordNumber(servitut.aktId || servitut.aktAliasId);

         availableDocuments.push({
            id: `servitut-${servitut.aktId || servitut.aktAliasId}`,
            title: `Servitut ${recordNo} - ${servitut.tekstSummarisk || (servitut.tekster && servitut.tekster.join(" ")) || "Servitut"}`,
            type: 'url',
            getUrl: () => tingbogsLink,
            category: 'Tingbog',
            useProxy: false,
            returnDoc: 'None',
         });
      }
   });

   // Add energy label documents directly into availableDocuments (guarded)
   const energyLabels = data?.EnergyLabel?.EnergyLabels || [];
   if (Array.isArray(energyLabels) && energyLabels.length > 0) {
      const filteredLabels = filterLatestEnergyLabels(energyLabels);
      if (Array.isArray(filteredLabels) && filteredLabels.length > 0) {
         const energySort = (a: any, b: any) => {
            const buildingNumberA = parseInt(a.Buildings?.[0]?.BuildingNumber?.replace(/^0+/, '') || '0', 10);
            const buildingNumberB = parseInt(b.Buildings?.[0]?.BuildingNumber?.replace(/^0+/, '') || '0', 10);
            return buildingNumberA - buildingNumberB;
         };

         const sortedLabels = filteredLabels.sort(energySort);
         sortedLabels.forEach((label: any) => {
            availableDocuments.push({
               id: `energy-label-pdf-${label.EnergyLabelSerialIdentifier}`,
               title: `Energy Label PDF - Building ${label.Buildings?.[0]?.BuildingNumber || 'N/A'}`,
               type: 'blob',
               getBlob: async () => {
                  const result = await PropertyInfoInterface.getEnergyLabelPdf(label.EnergyLabelSerialIdentifier);
                  if (!result) throw new Error('No PDF data received');
                  return result;
               },
               category: 'Energimærke',
               useProxy: true,
               returnDoc: 'pdf',
               selectable: true,
            });
         });
      }
   }

   if (availableDocuments.length === 0) {
      return (
         <div
            style={{
               padding: "12px 16px",
               textAlign: "center",
               color: "var(--almostBlack)",
               fontSize: "14px",
               fontStyle: "italic",
               backgroundColor: "var(--transBackground)",
               borderRadius: "var(--transBorderRadius)",
               border: "1px solid #e5e7eb",
            }}
         >
            {Localization.getText("No documents available")}
         </div>
      );
   }

   // Downloads documents sequentially, as some APIs do not handle parallel requests well.
   const handleDownloadDocuments = async (docs: documentData[]) => {
      try {
         const zip = new JSZip();
         // errors array removed; individual document errors are tracked in newDocErrors
         const newDocErrors: Record<string, string> = { ...docErrors };
         const totalDocs = docs.length;

         for (let i = 0; i < docs.length; i++) {
            const doc = docs[i];
            // Show progress before starting each document
            message.progress(Localization.getText("Downloading documents"), (i / totalDocs) * 100);
            try {
               let fileData: Blob | string | null = null;
               let fileName = doc.title?.replace(/[^a-zA-Z0-9_\-.]/g, "_") || doc.id;
               if (doc.type === "url" && doc.getUrl) {
                  const url = doc.getUrl();
                  try {
                     console.debug(`[DocumentsComponent] Downloading URL document:`, url, `useProxy=${doc.useProxy}`);
                     if (doc.useProxy) {
                        // Use the proxy helper when requested
                        const proxyResult = await PropertyInfoInterface.fetchViaProxy(doc) as Blob | null | undefined;
                        if (proxyResult instanceof Blob) {
                           fileData = proxyResult;
                           fileName += ".pdf";
                           delete newDocErrors[doc.id];
                        } else {
                           newDocErrors[doc.id] = "Ingen PDF tilgængelig, åben manuelt i browser.";
                           continue;
                        }
                     } else {
                        // Try to fetch directly from the URL. This may be blocked by CORS for some endpoints.
                        const resp = await fetch(url);
                        if (!resp.ok) {
                           newDocErrors[doc.id] = `Download failed (${resp.status})`;
                           continue;
                        }
                        const blob = await resp.blob();
                        fileData = blob;
                        // Use returnDoc to pick extension where possible
                        if (doc.returnDoc === 'png') fileName += '.png';
                        else if (doc.returnDoc === 'pdf') fileName += '.pdf';
                        else fileName += '.pdf';
                        delete newDocErrors[doc.id];
                     }
                  } catch (error) {
                     console.warn(`[DocumentsComponent] Failed to download URL document:`, url, error);
                     newDocErrors[doc.id] = "Ingen PDF tilgængelig, åben manuelt i browser.";
                     continue;
                  }
               } else if (doc.type === "blob" && doc.getBlob) {
                  try {
                     console.debug(`[DocumentsComponent] Downloading Blob document:`, doc.id);
                     fileData = await doc.getBlob();
                     // Use returnDoc to pick extension where possible
                     if (doc.returnDoc === 'png') fileName += '.png';
                     else if (doc.returnDoc === 'pdf') fileName += '.pdf';
                     else fileName += '.bin';
                     delete newDocErrors[doc.id];
                  } catch (error) {
                     console.warn(`[DocumentsComponent] Failed to download Blob document:`, doc.id, error);
                     newDocErrors[doc.id] = "Ingen PDF tilgængelig, åben manuelt i browser.";
                     continue;
                  }
               }

               if (fileData instanceof Blob) {
                  zip.file(fileName, fileData);
               } else if (typeof fileData === "string") {
                  zip.file(fileName, fileData);
               }
            } catch (err) {
               console.error(`[DocumentsComponent] Unexpected error in document download:`, doc.id, err);
               newDocErrors[doc.id] = "Ingen PDF tilgængelig, åben manuelt i browser.";
            }
            // Show progress after finishing each document
            message.progress(Localization.getText("Downloading documents"), ((i + 1) / totalDocs) * 100);
         }

         setDocErrors(newDocErrors);

         // Only download if at least one file is present in the zip
         if (Object.keys(zip.files).length > 0) {
            const zipBlob = await zip.generateAsync({ type: "blob" });
            const url = window.URL.createObjectURL(zipBlob);
            const a = document.createElement("a");
            a.href = url;

            // Use localization lookup for the base filename (add key "documents" to your locale files)
            const baseName = Localization.getText("Documents");

            // Try to extract SFE number from available data (coerce to digits only)
            const rawSfe = ((data && (data.BBRSFE?.[0]?.properties.BFEnummer)) || '');
            if (!rawSfe) console.warn('[DocumentsComponent] SFE value present but contains no digits:', rawSfe);

            // Timestamp YYYY-MM-DD_HH-MM (omit seconds)
            const now = new Date();
            const pad = (v: number) => String(v).padStart(2, '0');
            const ts = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}.${pad(now.getMinutes())}`;

            // Compose filename parts, include SFE only if digits were found
            let filename = baseName;
            if (rawSfe) filename += '_sfe' + rawSfe;
            filename += '_' + ts + '.zip';

            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
         } else {
            console.warn("No documents were added to the zip. Download cancelled.");
         }

         if (Object.keys(newDocErrors).length > 0) {
            console.warn("Some documents could not be downloaded:", newDocErrors);
         }
      } finally {
         // Hide progress bar when done, even on error
         message.progress();
      }
   };

   const handleDownloadSelectedDocuments = async () => {
      if (isDownloading) return;
      setIsDownloading(true);
      const docs = availableDocuments.filter((d) => selectedDocs.has(d.id));
      if (docs.length > 0) {
         await handleDownloadDocuments(docs);
      }
      setIsDownloading(false);
   };

   const toggleDoc = (docId: string) => {
      setSelectedDocs((prev) => {
         const newSet = new Set(prev);
         if (newSet.has(docId)) {
            newSet.delete(docId);
         } else {
            newSet.add(docId);
         }
         return newSet;
      });
   };

   // Group documents by category
   const documentsByCategory = availableDocuments.reduce<Record<string, documentData[]>>(
      (acc, doc) => {
         const cat = (doc as any).category || "Other";
         if (!acc[cat]) acc[cat] = [];
         acc[cat].push(doc);
         return acc;
      },
      {}
   );

   // Sort categories alphabetically
   const sortedCategories = Object.keys(documentsByCategory).sort((a, b) => a.localeCompare(b));

   return (
      <div>
         {/* Top Action Row: Select All + Download Buttons */}
         <GlassButtonRow
         >
            <ViewButtonCheck
               checked={
                  (() => {
                     const selectableIds = availableDocuments.filter(d => d.selectable).map(d => d.id);
                     const selectedPdfCount = Array.from(selectedDocs).filter(id => selectableIds.includes(id)).length;
                     return selectedPdfCount === selectableIds.length && selectableIds.length > 0 ? true : false;
                  })()
               }
               onCheck={() => {
                  // Only select PDFs (blob type)
                  const selectableIds = availableDocuments.filter(d => d.selectable).map(d => d.id);
                  const selectedPdfCount = Array.from(selectedDocs).filter(id => selectableIds.includes(id)).length;
                  if (selectedPdfCount === selectableIds.length && selectableIds.length > 0) {
                     setSelectedDocs(prev => {
                        const newSet = new Set(prev);
                        selectableIds.forEach(id => newSet.delete(id));
                        return newSet;
                     });
                  } else {
                     setSelectedDocs(prev => {
                        const newSet = new Set(prev);
                        selectableIds.forEach(id => newSet.add(id));
                        return newSet;
                     });
                  }
               }}
            >
               {(() => {
                  const selectableIds = availableDocuments.filter(d => d.selectable).map(d => d.id);
                  const selectedPdfCount = Array.from(selectedDocs).filter(id => selectableIds.includes(id)).length;
                  return selectedPdfCount === selectableIds.length && selectableIds.length > 0 ? "Afmarker Alle" : "Marker Alle";
               })()}
            </ViewButtonCheck>
            <GlassButton
               onClick={handleDownloadSelectedDocuments}
               disabled={selectedDocs.size === 0 || isDownloading}
            >
               <MIBIcon icon="download" />
               <span style={{ marginLeft: "5px" }}>Download udvalgte</span>
            </GlassButton>
            <div style={{ flex: 1 }} />
         </GlassButtonRow>

         {/* Document List by Category */}
         {sortedCategories.map((category) => (
            <div key={category} style={{ marginBottom: "16px" }}>
               <h3
                  style={{
                     fontWeight: 600,
                     fontSize: "14px",
                     margin: "8px 0",
                     color: "#111827",
                  }}
               >
                  {category}
               </h3>
               <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {documentsByCategory[category].map((doc) => {
                     const isChecked = selectedDocs.has(doc.id);
                     const handleChecked = (_checked: boolean) => {
                        toggleDoc(doc.id);
                     };
                     const errorMsg = docErrors[doc.id];
                     return (
                        <Fragment key={doc.id}>
                           <DocumentItem item={doc} checked={isChecked} onChecked={handleChecked} errorMsg={errorMsg} />
                        </Fragment>
                     );
                  })}
               </ul>
            </div>
         ))}
      </div>
   );
};


function DocumentItem(props: { item: documentData, onChecked?: (checked: boolean) => void, checked?: boolean, errorMsg?: string }) {
   let doc = props.item;

   // PDF detection: treat 'Planer', 'Miljø', and 'BBR Meddelelse' as PDF, 'Tingbog', 'BBR Kort', 'BBR Oplysninger' as URL

   // use the new selectable flag to determine whether the document can be checked for bulk download
   const isSelectable = !!doc.selectable;
   const accentColor = "var(--viamapGreen)";
   const isActionable = ((doc.type === "url" && doc.getUrl) || (doc.type === "blob" && doc.getBlob));

   return (
      <div
         key={doc.id}
         style={{
            display: "flex",
            alignItems: "center",
            padding: "6px 8px",
            marginBottom: "4px",
            border: "1px solid #e5e7eb",
            borderLeft: `4px solid ${accentColor}`,
            borderRadius: 6,
            gap: "8px",
            background: props.checked ? `${accentColor}20` : "#fff",
            cursor: "default",
            opacity: 1,
         }}
      >
         {isSelectable ? (
            <input
               type="checkbox"
               checked={props.checked}
               onClick={(e) => { e.stopPropagation(); }}
               onChange={() => props.onChecked?.(!props.checked)}
               style={{ accentColor: "var(--viamapGreen)" }}
               disabled={!doc.selectable}
            />
         ) : (
            <span style={{ width: "14px" }} />
         )}
         <DocumentIcon returnType={doc.returnDoc} />
         <span style={{ fontSize: "14px", color: "var(--almostBlack)", flex: 1 }}>
            {doc.title || doc.id}
         </span>
         {props.errorMsg && (
            <span style={{ color: "var(--dangerColor)", fontSize: "12px", fontStyle: "italic" }}>
               {props.errorMsg}
            </span>
         )}
         <ViewButtonIcon
            title={Localization.getText("Fetch")}
            onClick={async () => {
               try {
                  if (doc.type === 'url' && doc.getUrl) {
                     const url = doc.getUrl();
                     window.open(url, "_blank", "noopener,noreferrer");
                     return;
                  }
                  if (doc.type === 'blob' && doc.getBlob) {
                     const blob = await doc.getBlob();
                     const blobUrl = window.URL.createObjectURL(blob);
                     window.open(blobUrl, "_blank", "noopener,noreferrer");
                     setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
                     return;
                  }
                  // 'api' type removed from documents; no API-specific action here.
               } catch (err) {
                  console.error("Failed to open document:", err);
               }
            }}
            disabled={!isActionable}
         >
            <DocumentIcon returnType={doc.returnDoc} />
         </ViewButtonIcon>
      </div>
   );
}

function DocumentIcon(props: { returnType: documentData["returnDoc"] }) {

   switch (props.returnType) {
      case "pdf":
         return (
            <MIBIcon icon="pdf" color="var(--ownerShipBackground)" />
         );
      case "png":
         return (
            <MIBIcon icon="image" color="var(--ownerShipBackground)" />
         );
      default:
         return (
            <MIBIcon icon="link" color="var(--ownerShipBackground)" />
         );
   }
}